/**
 * StartScreen — Multi-Model Carousel Hero
 *
 * Layout:
 *  - Header bar: subtitle label left, Audi rings logo right
 *  - Hero carousel: model cards filling remaining height, swipeable
 *    - Prev / Next chevron buttons (only when >1 model)
 *    - Model name overlay (top of image)
 *    - Gradient scrim + consumption disclaimer + "Start" CTA (bottom)
 *
 * Transition: 380ms ease-out-quad, Tinder-style slide + scale.
 * Both cards (outgoing + entering) are in the DOM during transition.
 *
 * @see docs/ACTIVE_TASK.md — carousel architecture
 */
import type { CSSProperties } from 'react';
import { useState, useRef, useEffect } from 'react';
import { useAppStore } from '../../store/app.store';
import { tracker, EVENTS } from '../../services/tracking';
import { BrandButton } from '../ui/BrandButton';
import { useModelCarousel } from '../../hooks/useModelCarousel';
import type { ModelSummary, CarouselDirection } from '../../types/carousel';

const DURATION = 380;
const EASING = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';

/** CSS keyframes injected once into the document. */
const KEYFRAMES = `
  @keyframes _csEnterRight {
    from { transform: translateX(110%) scale(0.88); opacity: 0; }
    to   { transform: translateX(0)    scale(1);    opacity: 1; }
  }
  @keyframes _csEnterLeft {
    from { transform: translateX(-110%) scale(0.88); opacity: 0; }
    to   { transform: translateX(0)     scale(1);    opacity: 1; }
  }
  @keyframes _csExitLeft {
    from { transform: translateX(0) scale(1);    opacity: 1; }
    to   { transform: translateX(-110%) scale(0.88); opacity: 0; }
  }
  @keyframes _csExitRight {
    from { transform: translateX(0) scale(1);    opacity: 1; }
    to   { transform: translateX(110%)  scale(0.88); opacity: 0; }
  }
`;

function enterStyle(dir: CarouselDirection): CSSProperties {
  const name = dir === 'right' ? '_csEnterRight' : '_csEnterLeft';
  return { animation: `${name} ${DURATION}ms ${EASING} both` };
}
function exitStyle(dir: CarouselDirection): CSSProperties {
  const name = dir === 'right' ? '_csExitLeft' : '_csExitRight';
  return { animation: `${name} ${DURATION}ms ${EASING} both` };
}

// ---------------------------------------------------------------------------
// Inner hero card (not exported — only used inside StartScreen)
// ---------------------------------------------------------------------------

interface HeroCardProps {
  summary: ModelSummary;
  animStyle?: CSSProperties;
  zIndex: number;
  onStart: () => void;
  isLoading?: boolean;
  progress?: number;
  /** Label for the start button. Defaults to "Start". */
  startLabel?: string;
  /** Brand display name to prefix the model title, e.g. "Audi". */
  brandName?: string;
}

function HeroCard({
  summary,
  animStyle,
  zIndex,
  onStart,
  isLoading = false,
  progress = 0,
  startLabel = 'Start',
  brandName,
}: HeroCardProps) {
  return (
    <div
      className="absolute inset-0 rounded-t-2xl overflow-hidden"
      style={{ zIndex, ...animStyle }}
    >
      {/* Thumbnail */}
      <img
        src={summary.thumbnail ?? ''}
        alt={summary.displayName}
        className="absolute inset-0 w-full h-full object-cover object-center"
        draggable={false}
      />

      {/* Model name — top overlay */}
      <div className="absolute top-0 left-0 right-0 px-5 pt-22 landscape:pt-10 sm:pt-24 z-10 text-center pointer-events-none">
        <h1 className="text-white text-3xl font-bold leading-tight">
          {[brandName, summary.displayName].filter(Boolean).join(' ')}
        </h1>
      </div>

      {/* Gradient scrim + disclaimer + CTA — bottom overlay */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(to top, rgba(0,0,0,0.93) 0%, rgba(0,0,0,0.80) 18%, rgba(0,0,0,0.60) 35%, rgba(0,0,0,0.35) 52%, rgba(0,0,0,0.13) 70%, rgba(0,0,0,0.03) 85%, transparent 100%)',
          }}
        />
        <div className="relative px-5 pb-10 pt-20 flex flex-col gap-4">
          {summary.consumptionDisclaimer && (
            <p className="text-xs leading-snug text-brand-text/70">
              {summary.consumptionDisclaimer}
            </p>
          )}

          {isLoading ? (
            /* CI loading bar */
            <div className="w-full h-12 flex items-center">
              <div
                className="w-full h-1 rounded-full overflow-hidden"
                style={{ backgroundColor: '#101319' }}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${progress}%`,
                    backgroundColor: '#657081',
                    transition: 'width 600ms ease-out',
                  }}
                />
              </div>
            </div>
          ) : (
            <BrandButton onClick={onStart} className="self-center max-w-64">
              {startLabel}
            </BrandButton>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chevron SVG icons
// ---------------------------------------------------------------------------

function ChevronLeft() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-6 h-6"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-6 h-6"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// StartScreen
// ---------------------------------------------------------------------------

export function StartScreen() {
  const setScreen = useAppStore((s) => s.setScreen);
  const setSelectedModelId = useAppStore((s) => s.setSelectedModelId);
  const setConsumptionDisclaimer = useAppStore((s) => s.setConsumptionDisclaimer);
  const screen = useAppStore((s) => s.screen);
  const loadingProgress = useAppStore((s) => s.loadingProgress);
  const isLoading = screen === 'loading';

  const { summaries, loading, currentIndex, next, prev, logoLinkUrl, brandName, marketUi } =
    useModelCarousel();

  const [outgoing, setOutgoing] = useState<{
    summary: ModelSummary;
    direction: CarouselDirection;
  } | null>(null);
  const [enterDir, setEnterDir] = useState<CarouselDirection | null>(null);
  const [busy, setBusy] = useState(false);

  // Touch swipe
  const touchStartX = useRef<number | null>(null);
  const SWIPE_THRESHOLD = 50;

  function handleTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
  }
  function handleTouchEnd(e: React.TouchEvent) {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < SWIPE_THRESHOLD) return;
    navigate(delta < 0 ? 'next' : 'prev');
  }

  function navigate(action: 'next' | 'prev') {
    if (busy || isLoading || summaries.length <= 1) return;
    const current = summaries[currentIndex];
    const dir: CarouselDirection = action === 'next' ? 'right' : 'left';

    setOutgoing({ summary: current, direction: dir });
    setEnterDir(dir);
    setBusy(true);

    if (action === 'next') next();
    else prev();

    setTimeout(() => {
      setOutgoing(null);
      setEnterDir(null);
      setBusy(false);
    }, DURATION);
  }

  function handleStart() {
    // Store the currently-displayed model so useConfig re-fetches the right one.
    const currentModel = summaries[currentIndex];
    if (currentModel) {
      setSelectedModelId(currentModel.id);
      setConsumptionDisclaimer(currentModel.consumptionDisclaimer || null);
      // Fire model_selected tracking event (necessary — always sent)
      tracker.track(EVENTS.MODEL_SELECTED, { modelId: currentModel.id });
      // Sync the tracker's modelid so all subsequent events use the selected model
      tracker.updateModelId(currentModel.id);
    }
    setScreen('loading');
  }

  // Kiosk with single model: auto-start immediately (no carousel needed)
  const kioskMode = useAppStore((s) => s.kioskMode);
  const autoStarted = useRef(false);
  useEffect(() => {
    if (kioskMode && !loading && summaries.length === 1 && !autoStarted.current) {
      autoStarted.current = true;
      handleStart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once when summaries load
  }, [kioskMode, loading, summaries.length]);

  const currentSummary = summaries[currentIndex];
  const showNav = summaries.length > 1;

  return (
    <>
      <style>{KEYFRAMES}</style>
      <div
        className="h-full flex flex-col bg-black"
        style={{
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
      >
        {/* ── Header bar ──────────────────────────────────────────────── */}
        <header className="flex flex-col px-5 pt-2 pb-1.5 shrink-0 gap-1.5">
          {logoLinkUrl ? (
            <a href={logoLinkUrl} target="_blank" rel="noopener noreferrer" className="self-end">
              <img src="/app/logos/audi-logo-w2.png" alt="Audi" className="h-10 w-auto" />
            </a>
          ) : (
            <img src="/app/logos/audi-logo-w2.png" alt="Audi" className="h-10 w-auto self-end" />
          )}
          <div className="flex flex-col gap-0.1">
            <span className="text-brand-text text-base font-bold leading-snug">
              {marketUi.appTitle}
            </span>
            <span className="text-brand-text/75 text-xs leading-normal">
              {marketUi.appSubtitle}
            </span>
          </div>
        </header>

        {/* ── Hero carousel ───────────────────────────────────────────── */}
        <div
          className="grow relative overflow-hidden bg-black rounded-t-2xl"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Loading skeleton */}
          {loading && <div className="absolute inset-0 bg-zinc-900 animate-pulse" />}

          {/* Outgoing card — exits with slide animation */}
          {outgoing && (
            <HeroCard
              summary={outgoing.summary}
              animStyle={exitStyle(outgoing.direction)}
              zIndex={1}
              onStart={handleStart}
              isLoading={isLoading}
              progress={loadingProgress}
              startLabel={marketUi.startButton}
              brandName={brandName}
            />
          )}

          {/* Current card — enters with slide animation (or static on first load) */}
          {currentSummary && (
            <HeroCard
              key={currentSummary.id}
              summary={currentSummary}
              animStyle={enterDir ? enterStyle(enterDir) : undefined}
              zIndex={2}
              onStart={handleStart}
              isLoading={isLoading}
              progress={loadingProgress}
              startLabel={marketUi.startButton}
              brandName={brandName}
            />
          )}

          {/* ── Prev / Next nav buttons ─────────────────────────────── */}
          {showNav && (
            <>
              <button
                onClick={() => navigate('prev')}
                disabled={busy}
                aria-label="Previous model"
                style={{ left: 'calc(0.75rem + env(safe-area-inset-left))' }}
                className="
                  absolute top-1/2 -translate-y-1/2 z-20
                  flex items-center justify-center
                  w-10 h-10 rounded-full
                  bg-black/25 hover:bg-black/40 active:bg-black/55
                  text-white/80 hover:text-white
                  backdrop-blur-sm
                  transition-all duration-150
                  disabled:opacity-30 disabled:pointer-events-none
                "
              >
                <ChevronLeft />
              </button>

              <button
                onClick={() => navigate('next')}
                disabled={busy}
                aria-label="Next model"
                style={{ right: 'calc(0.75rem + env(safe-area-inset-right))' }}
                className="
                  absolute top-1/2 -translate-y-1/2 z-20
                  flex items-center justify-center
                  w-10 h-10 rounded-full
                  bg-black/25 hover:bg-black/40 active:bg-black/55
                  text-white/80 hover:text-white
                  backdrop-blur-sm
                  transition-all duration-150
                  disabled:opacity-30 disabled:pointer-events-none
                "
              >
                <ChevronRight />
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
